import React, { useState, useEffect } from 'react';
import Modal from '../../components/Modal';
import { BASE_DOMAIN, autoGenDnsPattern, isBaseDomain } from '../../utility/baseDomain';
import { useToast } from '../../components/ToastContext';
import axiosInstance from '../../utility/axiosInstance';
import { handleAxiosError } from '../../helpers/handleAxiosError';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInstances } from '../../store/slices/instancesSlice';
import { fetchDomains } from '../../store/slices/domainsSlice';
import { AppDispatch, RootState } from '../../store/store';
import { fetchSecurityGroupRules } from '../../store/slices/securityRulesSlice';

interface DNSFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    editingDNS: { id: number; name: string; port: number } | null;
    instanceId: string;
    instances: any[];
    proxyHosts: any[];
    existingDNS: { id: number; name: string; port: number }[];
    loading: boolean;
    setLoading: (loading: boolean) => void;
}

type VerifyStep = 'idle' | 'loading-challenge' | 'challenge' | 'verifying' | 'verified' | 'failed';

const isCustomDomain = (domain: string) =>
    domain.trim().length > 0 && !isBaseDomain(domain);

const DNSFormModal: React.FC<DNSFormModalProps> = ({
    isOpen, onClose, editingDNS, instanceId, instances, proxyHosts, existingDNS, loading, setLoading,
}) => {
    const { showToast } = useToast();
    const dispatch = useDispatch<AppDispatch>();
    const verifiedDomains = useSelector((s: RootState) => s.domains.domains);

    const [formData, setFormData] = useState({ name: '', port: 8080 });
    const [verifyStep, setVerifyStep] = useState<VerifyStep>('idle');
    // `hint`: the record's value changed for this account (ROADMAP 91), when its pending claim is from before the change.
    const [challenge, setChallenge] = useState<{ record_name: string; record_value: string; hint?: string } | null>(null);

    useEffect(() => {
        if (isOpen) {
            setFormData({ name: editingDNS?.name || '', port: editingDNS?.port || 8080 });
            setVerifyStep('idle');
            setChallenge(null);
            dispatch(fetchDomains());
        }
    }, [isOpen, editingDNS, dispatch]);

    // When domain input changes, reset verification state
    useEffect(() => {
        setVerifyStep('idle');
        setChallenge(null);
    }, [formData.name]);

    const isDomainVerified = (domain: string) =>
        verifiedDomains.some(d => d.domain === domain && d.status === 'verified');

    const isDomainPending = (domain: string) =>
        verifiedDomains.some(d => d.domain === domain && d.status === 'pending');

    const generateShortId = (length = 8) => {
        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    };

    const generateDnsName = () => {
        const instance = instances.find(i => i.id === instanceId);
        const base = instance?.name ? instance.name.toLowerCase().replace(/[^a-z0-9-]/g, '-') : 'instance';
        return `${base}-${generateShortId(8)}.${BASE_DOMAIN}`;
    };

    const handleGetChallenge = async () => {
        setVerifyStep('loading-challenge');
        try {
            const res = await axiosInstance.get(`/v1/dns/verify-domain?domain=${encodeURIComponent(formData.name)}`);
            setChallenge(res.data);
            setVerifyStep('challenge');
        } catch (e: any) {
            showToast(e.response?.data?.error || 'Failed to load verification challenge', 'error');
            setVerifyStep('idle');
        }
    };

    const handleVerify = async () => {
        setVerifyStep('verifying');
        try {
            await axiosInstance.post('/v1/dns/verify-domain', { domain: formData.name });
            setVerifyStep('verified');
            showToast(`${formData.name} verified`, 'success');
            dispatch(fetchDomains());
        } catch (e: any) {
            // The whole answer, so the toast carries its hint too (a record that still holds the old value says what to put).
            const data = e.response?.data;
            showToast(data && typeof data === 'object' ? data : 'Verification failed', 'error');
            setVerifyStep('failed');
        }
    };

    const canSubmit = () => {
        if (!formData.name) return true; // auto-generate allowed
        if (!isCustomDomain(formData.name)) return true; // .redu.cloud always ok
        return isDomainVerified(formData.name) || verifyStep === 'verified';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const fqdnPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

        if (formData.name && isBaseDomain(formData.name) && !autoGenDnsPattern.test(formData.name)) {
            showToast(`DNS name cannot end with .${BASE_DOMAIN}. Leave it empty for a random one, or click ⟳ Generate.`, 'error');
            return;
        }
        if (formData.name && !isBaseDomain(formData.name) && !fqdnPattern.test(formData.name)) {
            showToast('Domain name is not a valid FQDN.', 'error');
            return;
        }
        if (formData.port) {
            const portNum = Number(formData.port);
            if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
                showToast('Port must be a valid TCP port number (1-65535)', 'error');
                return;
            }
            const isDuplicatePort = existingDNS.some(dns => dns.port === portNum && dns.id !== editingDNS?.id);
            if (isDuplicatePort) {
                showToast(`Port ${portNum} is already in use by another DNS record for this instance.`, 'error');
                return;
            }
        }

        setLoading(true);
        try {
            if (editingDNS) {
                await axiosInstance.post('/v1/dns/update', {
                    instanceId, proxyHostId: String(editingDNS.id), newDomain: formData.name, newPort: formData.port,
                });
                showToast('DNS updated!', 'success');
            } else {
                await axiosInstance.post('/v1/dns/create', {
                    instanceId, forwardPort: formData.port, domain: formData.name,
                });
                showToast('DNS created!', 'success');
            }
            dispatch(fetchInstances());
            dispatch(fetchSecurityGroupRules());
            onClose();
        } catch (err) {
            showToast(handleAxiosError(err), 'error');
        } finally {
            setLoading(false);
        }
    };

    const showVerification = isCustomDomain(formData.name) && !isDomainVerified(formData.name);
    const isPending = isCustomDomain(formData.name) && isDomainPending(formData.name);
    const alreadyVerified = isCustomDomain(formData.name) && isDomainVerified(formData.name);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={editingDNS ? "Edit DNS Record" : "Create DNS Record"}>
            <form onSubmit={handleSubmit} className="create-form">
                <div className="form-group">
                    <label>Domain Name (Optional)</label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                            placeholder="e.g. app.yourcompany.com or leave empty for a .redu.cloud domain"
                            style={{ flex: '1 1 200px' }}
                        />
                        <button
                            type="button"
                            onClick={() => setFormData({ ...formData, name: generateDnsName() })}
                            title="Generate a random .redu.cloud domain"
                            className="btn btn-primary"
                            style={{ whiteSpace: 'nowrap', color: 'white', fontSize: '0.75rem', maxHeight: '40px', flex: '1 1 auto' }}
                        >
                            ⟳ Generate
                        </button>
                    </div>

                    {/* Already pending - show shortcut to Domains page */}
                    {isPending && !isDomainVerified(formData.name) && (
                        <div style={{ marginTop: '10px', padding: '10px 14px', borderRadius: '8px', background: 'rgba(237,137,54,0.1)', border: '1px solid rgba(237,137,54,0.4)', fontSize: '0.875rem', color: '#ed8936' }}>
                            ⏳ Verification pending - <a href="/category/user/domains" target="_blank" rel="noopener noreferrer" style={{ color: '#ed8936', fontWeight: 600 }}>check status in Domains →</a>
                        </div>
                    )}

                    {/* Already verified */}
                    {alreadyVerified && (
                        <div style={{ marginTop: '10px', padding: '10px 14px', borderRadius: '8px', background: 'rgba(72,187,120,0.1)', border: '1px solid rgba(72,187,120,0.4)', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem', color: '#48bb78' }}>
                            <span>✓</span> <span>Domain verified - you own this domain</span>
                        </div>
                    )}

                    {/* Needs verification */}
                    {showVerification && (
                        <div style={{ marginTop: '12px', padding: '14px 16px', borderRadius: '8px', background: 'var(--surface-alt)', border: '1px solid var(--border)' }}>
                            <p style={{ margin: '0 0 10px', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                                Custom domain - verify ownership first
                            </p>

                            {verifyStep === 'idle' || verifyStep === 'failed' ? (
                                <>
                                    <p style={{ margin: '0 0 10px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                                        Add a TXT record to your DNS provider to prove you own <strong style={{ color: 'var(--text-primary)' }}>{formData.name}</strong>.
                                    </p>
                                    <button type="button" onClick={handleGetChallenge} disabled={false} style={{ padding: '7px 14px', background: 'var(--surface-elevated)', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.85rem' }}>
                                        Get TXT record
                                    </button>
                                    {verifyStep === 'failed' && (
                                        <p style={{ margin: '8px 0 0', fontSize: '0.8rem', color: 'var(--color-error, #e53e3e)' }}>
                                            TXT record not found. Make sure it is saved and try again.
                                        </p>
                                    )}
                                </>
                            ) : verifyStep === 'loading-challenge' ? (
                                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-muted)' }}>Loading…</p>
                            ) : verifyStep === 'verified' ? (
                                <p style={{ margin: 0, fontSize: '0.875rem', color: '#48bb78', fontWeight: 600 }}>✓ Verified</p>
                            ) : challenge ? (
                                <>
                                    <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border)', borderRadius: '6px', padding: '10px 12px', marginBottom: '10px', fontSize: '0.8rem' }}>
                                        <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                                            <span style={{ width: '60px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.72rem' }}>Type</span>
                                            <code>TXT</code>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                                            <span style={{ width: '60px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.72rem', paddingTop: '2px' }}>Name</span>
                                            <code style={{ wordBreak: 'break-all', cursor: 'pointer' }} title="Click to copy" onClick={() => { navigator.clipboard.writeText(challenge.record_name); showToast('Copied', 'success'); }}>
                                                {challenge.record_name}
                                            </code>
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            <span style={{ width: '60px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.72rem', paddingTop: '2px' }}>Value</span>
                                            <code style={{ wordBreak: 'break-all', cursor: 'pointer' }} title="Click to copy" onClick={() => { navigator.clipboard.writeText(challenge.record_value); showToast('Copied', 'success'); }}>
                                                {challenge.record_value}
                                            </code>
                                        </div>
                                    </div>
                                    {challenge.hint && (
                                        <p style={{ margin: '0 0 10px', fontSize: '0.8rem', color: 'var(--text-primary)', lineHeight: 1.5 }}>{challenge.hint}</p>
                                    )}
                                    <button type="button" onClick={handleVerify} disabled={verifyStep === 'verifying'} style={{ padding: '7px 14px', background: 'var(--accent)', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
                                        {verifyStep === 'verifying' ? 'Checking…' : 'Verify'}
                                    </button>
                                </>
                            ) : null}
                        </div>
                    )}
                </div>

                <div className="form-group">
                    <label>Port</label>
                    <input
                        type="number"
                        value={formData.port}
                        onChange={e => setFormData({ ...formData, port: Number(e.target.value) })}
                        required min="1" max="65535"
                    />
                </div>

                <p className="help-text">
                    Configure custom domains and ports for your instance.{' '}
                    Need to verify a domain first?{' '}
                    <a href="/category/user/domains" target="_blank" rel="noopener noreferrer">Manage your domains →</a>
                </p>

                <div className="modal-actions">
                    <button type="button" className="cancel-button" onClick={onClose} disabled={loading}>Cancel</button>
                    <button className="submit-button" disabled={loading || !canSubmit()} type="submit">
                        {loading ? 'Saving...' : editingDNS ? 'Update DNS' : 'Create DNS'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default DNSFormModal;
